<template>
  <div class="cb-wrapper">
    <div class="cb-layout">

      <!-- ===== LEFT SIDEBAR (identical to localhost:3000/indicator) ===== -->
      <div class="cb-sidebar">

        <!-- Interval -->
        <div class="cb-filter-group">
          <div class="cb-filter-label">Interval</div>
          <select class="cb-select" v-model="config.interval" @change="fetchData">
            <option value="year">Yearly</option>
            <option value="quartal">Quarterly</option>
            <option value="month">Monthly</option>
            <option value="day">Daily</option>
          </select>
        </div>

        <!-- Custom Time -->
        <div class="cb-filter-group">
          <div class="cb-filter-label">Custom Time</div>
          <div class="cb-time-row">
            <div class="cb-time-col">
              <div class="cb-time-sub">From</div>
              <input class="cb-input" type="number" v-model.number="config.time_from" placeholder="e.g. 2010" @change="fetchData" />
            </div>
            <div class="cb-time-col">
              <div class="cb-time-sub">To</div>
              <input class="cb-input" type="number" v-model.number="config.time_to" placeholder="e.g. 2023" @change="fetchData" />
            </div>
          </div>
        </div>

        <!-- Province Search + List -->
        <div class="cb-region-box">
          <div class="cb-search-wrap">
            <input
              type="text"
              class="cb-search-input"
              placeholder="Search province"
              v-model="searchKey"
              @input="onSearch"
            />
            <span class="cb-search-icon">&#128269;</span>
          </div>

          <!-- Selected count badge -->
          <div v-if="selectedCount > 0" class="cb-selected-bar">
            <span>{{ selectedCount }} province selected</span>
            <button class="cb-clear-btn" @click="clearProvinces">&#10005;</button>
          </div>

          <!-- Province list -->
          <div class="cb-region-list">
            <!-- Indonesia (national) -->
            <label class="cb-region-item" :class="{ 'cb-region-checked': isNationalSelected }">
              <input
                class="cb-hidden-chk"
                type="checkbox"
                id="prov-nat"
                :value="0"
                v-model="selectedProvinces"
                @change="fetchData"
              />
              <span class="cb-check-box">
                <span v-if="isNationalSelected" class="cb-check-mark">&#10003;</span>
              </span>
              <span>Indonesia</span>
            </label>

            <!-- Other provinces -->
            <label
              class="cb-region-item"
              :class="{ 'cb-region-checked': selectedProvinces.includes(parseInt(prov.code)) }"
              v-for="prov in filteredProvinces"
              :key="prov.code"
            >
              <input
                class="cb-hidden-chk"
                type="checkbox"
                :id="'prov-' + prov.code"
                :value="parseInt(prov.code)"
                v-model="selectedProvinces"
                @change="fetchData"
              />
              <span class="cb-check-box">
                <span v-if="selectedProvinces.includes(parseInt(prov.code))" class="cb-check-mark">&#10003;</span>
              </span>
              <span>{{ prov.name }}</span>
            </label>
          </div>
        </div>

      </div><!-- /cb-sidebar -->

      <!-- ===== RIGHT CONTENT ===== -->
      <div class="cb-content">
        <!-- Title -->
        <h4 class="cb-chart-title">{{ title }}</h4>
        
        <!-- Loading -->
        <div v-if="loading" class="cb-loading">
          <div class="cb-spinner"></div>
          <span>Memuat data...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!series || series.length === 0" class="cb-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          <p>Tidak ada data untuk filter yang dipilih.</p>
        </div>

        <!-- Chart -->
        <vue-apex-charts
          v-else
          ref="apexChart"
          type="line"
          height="320"
          :options="chartOptions"
          :series="series"
        />

      </div><!-- /cb-content -->

    </div><!-- /cb-layout -->
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import axios from 'axios'
import FormulaBuilder from './FormulaBuilder.vue'

const dynamicHost = typeof window !== 'undefined' && window.location && window.location.hostname ? window.location.hostname : 'localhost'
const API_BASE = process.env.VUE_APP_API_URL && !process.env.VUE_APP_API_URL.includes('localhost') ? process.env.VUE_APP_API_URL.replace(/\/$/, '') : `http://${dynamicHost}:3008`

export default {
  name: 'ChartBuilder',
  components: { VueApexCharts },
  props: {
    indicatorId: { type: Number, required: true },
    title: { type: String, default: 'Grafik Data' },
    indicators: { type: Array, default: () => [] },
    value: {
      type: Object,
      default: () => ({
        interval: 'year',
        unit: 'province',
        time_from: null,
        time_to: null,
        provinces: [0],
        use_formula: false,
        formula: []
      })
    }
  },
  data() {
    return {
      config: {
        interval: 'year',
        unit: 'province',
        time_from: null,
        time_to: null,
        provinces: [0],
        use_formula: false,
        formula: [],
        ...(this.value || {})
      },
      activeTab: 'chart',
      searchKey: '',
      provinces: [],        // all provinces from API
      displayProvinces: [], // filtered by search
      selectedProvinces: [0], // codes of selected; 0 = Indonesia
      loading: false,
      series: [],
      chartOptions: {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: true }
        },
        stroke: { curve: 'smooth', width: 2.5 },
        colors: ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'],
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false,
            datetimeFormatter: { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM' }
          }
        },
        yaxis: {
          labels: {
            formatter: v => v !== null && v !== undefined ? Number(v).toLocaleString('id-ID') : ''
          }
        },
        legend: { position: 'top', horizontalAlign: 'left' },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          y: { formatter: v => Number(v).toLocaleString('id-ID') }
        },
        grid: { borderColor: '#f0f0f0' }
      }
    }
  },
  computed: {
    isNationalSelected() {
      return this.selectedProvinces.includes(0)
    },
    selectedCount() {
      return this.selectedProvinces.filter(c => c !== 0).length
    },
    filteredProvinces() {
      if (!this.searchKey) return this.provinces
      const q = this.searchKey.toLowerCase()
      return this.provinces.filter(p => p.name.toLowerCase().includes(q))
    }
  },
  watch: {
    indicatorId: {
      handler(v) { if (v) this.fetchData() },
      immediate: true
    },
    selectedProvinces() {
      this.syncConfig()
    },
    'config.interval'() { this.syncConfig() },
    'config.unit'() { this.syncConfig() },
    'config.time_from'() { this.syncConfig() },
    'config.time_to'() { this.syncConfig() },
  },
  mounted() {
    this.loadProvinces()
    if (this.value && this.value.provinces) {
      this.selectedProvinces = [...this.value.provinces]
    }
  },
  methods: {
    syncConfig() {
      const newVal = {
        ...this.config,
        provinces: [...this.selectedProvinces]
      }
      this.$emit('input', newVal)
    },

    onSearch() {
      // filtering is handled by computed filteredProvinces
    },

    clearProvinces() {
      this.selectedProvinces = [0]
      this.fetchData()
    },

    loadProvinces() {
      axios.get(`${API_BASE}/admin/v1/province?per_page=100&orderby=name&sort=asc`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      }).then(res => {
        this.provinces = res.data.data ? (res.data.data.rows || res.data.data) : []
        this.displayProvinces = this.provinces
      }).catch(() => {
        this.provinces = []
      })
    },

    fetchData() {
      if (!this.indicatorId) return
      this.loading = true

      const params = new URLSearchParams()
      params.append('indicator_id', this.indicatorId)
      params.append('interval', this.config.interval || 'year')
      if (this.config.time_from) params.append('from', this.config.time_from)
      if (this.config.time_to) params.append('to', this.config.time_to)
      
      const provinces = this.selectedProvinces.length > 0 ? this.selectedProvinces : [0]
      provinces.forEach(code => params.append('provinces[]', code))

      axios.get(`${API_BASE}/v1/indicator_data/chart?${params.toString()}`)
        .then(res => {
          const raw = res.data.data
          if (!raw || typeof raw !== 'object' || Object.keys(raw).length === 0) { this.series = []; return }

          const newSeries = []
          Object.keys(raw).forEach(regionName => {
            const regionData = raw[regionName]
            if (Array.isArray(regionData) && regionData.length > 0) {
              newSeries.push({
                name: regionName,
                data: regionData.map(v => [new Date(v.date).getTime(), parseFloat(v.value) || 0])
              })
            }
          })

          this.series = newSeries
        })
        .catch(() => { this.series = [] })
        .finally(() => { this.loading = false })
    }
  }
}
</script>

<style scoped>
/* ===== Wrapper ===== */
.cb-wrapper {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}

.cb-layout {
  display: flex;
  min-height: 480px;
}

/* ===== Left Sidebar ===== */
.cb-sidebar {
  width: 270px;
  flex-shrink: 0;
  border-right: 1px solid #f0f0f0;
  background: #fff;
  padding: 18px 16px;
  overflow-y: auto;
  max-height: 580px;
}

.cb-filter-group {
  margin-bottom: 18px;
}

.cb-filter-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #F59E0B;
  text-transform: uppercase;
  margin-bottom: 7px;
}

.cb-select {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  color: #333;
  background: #f9f9f9;
  cursor: pointer;
  outline: none;
  appearance: auto;
}
.cb-select:focus { border-color: #F59E0B; }

.cb-time-row { display: flex; gap: 8px; }
.cb-time-col { flex: 1; }
.cb-time-sub {
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
}

.cb-input {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  color: #333;
  background: #f9f9f9;
  outline: none;
}
.cb-input:focus { border-color: #F59E0B; }

/* Region box */
.cb-region-box {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}

.cb-search-wrap {
  position: relative;
  border-bottom: 1px solid #eee;
}
.cb-search-input {
  width: 100%;
  border: none;
  padding: 8px 36px 8px 12px;
  font-size: 12px;
  outline: none;
  background: #f9f9f9;
}
.cb-search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #F59E0B;
  pointer-events: none;
}

.cb-selected-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FEF3C7;
  padding: 5px 10px;
  font-size: 11px;
  color: #92400E;
  border-bottom: 1px solid #FDE68A;
}
.cb-clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #92400E;
  font-size: 13px;
  padding: 0;
  line-height: 1;
}

.cb-region-list {
  max-height: 220px;
  overflow-y: auto;
}

/* Custom checkbox rows — exactly like indicator page */
.cb-region-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.12s;
  user-select: none;
}
.cb-region-item:last-child { border-bottom: none; }
.cb-region-item:hover { background: #FFFBEB; }
.cb-region-checked { background: #FEF3C7 !important; }

.cb-hidden-chk { display: none; }

.cb-check-box {
  width: 16px;
  height: 16px;
  border: 2px solid #D1D5DB;
  border-radius: 3px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.12s, background 0.12s;
}
.cb-region-checked .cb-check-box {
  border-color: #F59E0B;
  background: #F59E0B;
}
.cb-check-mark {
  color: #fff;
  font-size: 10px;
  line-height: 1;
}

/* ===== Right Content ===== */
.cb-content {
  flex: 1;
  padding: 20px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cb-chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 14px;
}

/* Loading spinner */
.cb-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #F59E0B;
  min-height: 240px;
}
.cb-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #FEF3C7;
  border-top-color: #F59E0B;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Empty state */
.cb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 10px;
  color: #aaa;
  min-height: 240px;
}
.cb-empty svg { opacity: 0.4; }
.cb-empty p { font-size: 13px; margin: 0; }

/* Tab bar */
.cb-tab-bar {
  display: flex;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  margin-top: 14px;
  background: #fff;
}
.cb-tab-btn {
  flex: 1;
  padding: 9px 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.15s, border-bottom 0.15s;
  border-bottom: 3px solid transparent;
}
.cb-tab-active {
  color: #F59E0B !important;
  border-bottom: 3px solid #F59E0B !important;
  background: #FFFBEB;
}

/* Highlight / source */
.cb-highlight { padding: 10px 0 0; }
.cb-highlight-input {
  width: 100%;
  border: none;
  border-bottom: 1px dashed #ddd;
  background: transparent;
  font-size: 12px;
  color: #555;
  padding: 6px 0;
  outline: none;
}
.cb-source-info { padding: 12px 0 0; }
</style>
